import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

// Common timezones with user-friendly names
const TIMEZONES = [
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)' },
  { value: 'Europe/London', label: 'Greenwich Mean Time (GMT)' },
  { value: 'Europe/Paris', label: 'Central European Time (CET)' },
  { value: 'Europe/Berlin', label: 'Central European Time (CET)' },
  { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)' },
  { value: 'Asia/Shanghai', label: 'China Standard Time (CST)' },
  { value: 'Asia/Kolkata', label: 'India Standard Time (IST)' },
  { value: 'Australia/Sydney', label: 'Australian Eastern Time (AET)' },
  { value: 'Australia/Perth', label: 'Australian Western Time (AWT)' },
  { value: 'Pacific/Auckland', label: 'New Zealand Standard Time (NZST)' },
];

interface TimezoneSelectorProps {
  onTimezoneChange?: (timezone: string) => void;
  showCurrentTime?: boolean;
  allowSave?: boolean; // New prop to control whether to save to backend
}

const TimezoneSelector: React.FC<TimezoneSelectorProps> = ({ 
  onTimezoneChange, 
  showCurrentTime = true,
  allowSave = true 
}) => {
  const { token } = useAuth();
  const [selectedTimezone, setSelectedTimezone] = useState<string>('America/Los_Angeles');
  const [currentTime, setCurrentTime] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // Fetch current timezone on component mount
  useEffect(() => {
    fetchCurrentTimezone();
  }, []);

  // Update current time display
  useEffect(() => {
    if (showCurrentTime && selectedTimezone) {
      const updateTime = () => {
        try {
          const now = new Date();
          const timeString = now.toLocaleTimeString('en-US', {
            timeZone: selectedTimezone,
            hour12: true,
            hour: 'numeric',
            minute: '2-digit',
            second: '2-digit'
          });
          setCurrentTime(timeString);
        } catch (error) {
          console.error('Error formatting time:', error);
          setCurrentTime('Error displaying time');
        }
      };

      updateTime();
      const interval = setInterval(updateTime, 1000);
      return () => clearInterval(interval);
    }
  }, [selectedTimezone, showCurrentTime]);

  const fetchCurrentTimezone = async () => {
    if (!token) return;

    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/timezone`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedTimezone(data.timezone);
      } else {
        console.error('Failed to fetch timezone');
      }
    } catch (error) {
      console.error('Error fetching timezone:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTimezoneChange = async (event: any) => {
    const newTimezone = event.target.value;
    setSelectedTimezone(newTimezone);
    setError('');
    setSuccess('');

    // Call the callback immediately for signup flow
    if (onTimezoneChange) {
      onTimezoneChange(newTimezone);
    }

    // Only save to backend if allowSave is true and user is authenticated
    if (!allowSave) {
      return; // Just update local state, don't save to backend
    }

    if (!token) {
      setError('Authentication required to save timezone preference');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/timezone`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ timezone: newTimezone })
      });

      if (response.ok) {
        setSuccess('Timezone updated successfully!');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to update timezone');
      }
    } catch (error) {
      console.error('Error updating timezone:', error);
      setError('Failed to update timezone. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getTimezoneLabel = (value: string) => {
    const timezone = TIMEZONES.find(tz => tz.value === value);
    return timezone ? timezone.label : value;
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 400 }}>
      <Typography variant="h6" gutterBottom>
        Timezone Settings
      </Typography>
      
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="timezone-select-label">Timezone</InputLabel>
        <Select
          labelId="timezone-select-label"
          id="timezone-select"
          value={selectedTimezone}
          label="Timezone"
          onChange={handleTimezoneChange}
          disabled={loading}
        >
          {TIMEZONES.map((timezone) => (
            <MenuItem key={timezone.value} value={timezone.value}>
              {timezone.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {showCurrentTime && currentTime && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Current time in {getTimezoneLabel(selectedTimezone)}: {currentTime}
          </Typography>
        </Box>
      )}

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <CircularProgress size={20} sx={{ mr: 1 }} />
          <Typography variant="body2">Updating timezone...</Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary">
        This timezone will be used for displaying times in SMS notifications and the dashboard.
      </Typography>
    </Box>
  );
};

export default TimezoneSelector;
